// Layout + auth guard for everything under /app. Anonymous users get
// bounced to /login. Users without a workspace get bounced to /onboarding.
//
// v0.6.3 shell — slim top bar (Buffer-style) with a contextual brand
// bar that appears for /app/brands/{id}/* pages.
import { useEffect, useRef, useState } from "react";
import { Outlet, Navigate, createFileRoute, useRouterState } from "@tanstack/react-router";
import { AppTopBar } from "../../components/AppTopBar";
import { BrandContextBar } from "../../components/BrandContextBar";
import { useSession } from "../../lib/session";
import { workspaces as wsAPI } from "../../lib/api";

function AppShell() {
  const s = useSession();

  // Defensive workspace auto-select (preserved from v0.5.x).
  const switched = useRef(false);
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    if (switched.current) return;
    if (s.state !== "signed") return;
    if (s.me.workspaces.length === 0) return;
    if (s.me.current_workspace_id) return;
    switched.current = true;
    setSwitching(true);
    void (async () => {
      try {
        await wsAPI.switch({ workspace_id: s.me.workspaces[0].id });
        await s.refresh();
      } finally {
        setSwitching(false);
      }
    })();
  }, [s]);

  // Mobile drawer state. Owned here so AppTopBar's hamburger can toggle
  // it and pages can close it via route change.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Detect /app/brands/{id} (with optional /calendar /comments /gallery subroute)
  // so we can render BrandContextBar inline below the top bar.
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const brandMatch = /^\/app\/brands\/([0-9a-f-]{36})(?:\/(calendar|comments|gallery))?\/?$/.exec(pathname);
  const activeBrandId = brandMatch?.[1] ?? null;
  const activeBrandSubroute =
    (brandMatch?.[2] as "calendar" | "comments" | "gallery" | undefined) ?? null;

  if (s.state === "loading") return null;
  if (s.state === "anon") return <Navigate to="/login" replace />;
  if (s.me.workspaces.length === 0) return <Navigate to="/onboarding" replace />;
  if (!s.me.current_workspace_id || switching) return null;

  const currentWs =
    s.me.workspaces.find((w) => w.id === s.me.current_workspace_id) ?? s.me.workspaces[0];

  return (
    <div className="min-h-full flex flex-col">
      <AppTopBar
        workspaceName={currentWs?.name ?? ""}
        userEmail={s.me.user.email}
        onSignOut={() => void s.signOut()}
        mobileOpen={mobileNavOpen}
        onMobileToggle={() => setMobileNavOpen((v) => !v)}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      {activeBrandId ? (
        <BrandContextBar brandId={activeBrandId} subroute={activeBrandSubroute ?? null} />
      ) : null}
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute("/app")({
  component: AppShell,
});
